import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getMarketWatchStatus } from "../api/marketWatch";

/**
 * Is market watch (Etsy trends) turned on for this account? Admin only lookup
 * used by the main nav ("Market watch") and the page redirect. Module cache so
 * every mount shares one request per account. Defaults to OFF while unknown or
 * on error, and never calls the API for non admins.
 */
let cache = { enterpriseId: "", enabled: false, loaded: false };
let inflight = null;
const listeners = new Set();

const notify = () => {
  listeners.forEach((fn) => {
    try {
      fn(cache);
    } catch {
      /* ignore */
    }
  });
};

export const setMarketWatchEnabledCache = (enabled, enterpriseId) => {
  cache = {
    enterpriseId: String(enterpriseId ?? cache.enterpriseId ?? ""),
    enabled: enabled === true,
    loaded: true,
  };
  notify();
};

/** Test hook. */
export const resetMarketWatchEnabledCache = () => {
  cache = { enterpriseId: "", enabled: false, loaded: false };
  inflight = null;
};

/** The status body means "on" only when the server says enabled: true. */
export const isMarketWatchOn = (body) => body?.enabled === true;

export function loadMarketWatchEnabled(enterpriseId, isAdmin, fetcher = getMarketWatchStatus) {
  const eid = String(enterpriseId || "");
  if (!isAdmin || !eid) return Promise.resolve(false);
  if (cache.loaded && cache.enterpriseId === eid) return Promise.resolve(cache.enabled);
  if (inflight && inflight.enterpriseId === eid) return inflight.promise;
  const promise = Promise.resolve()
    .then(() => fetcher("daily"))
    .then(isMarketWatchOn)
    .catch(() => false)
    .then((on) => {
      setMarketWatchEnabledCache(on, eid);
      return on;
    })
    .finally(() => {
      if (inflight?.promise === promise) inflight = null;
    });
  inflight = { enterpriseId: eid, promise };
  return promise;
}

export function subscribeMarketWatchEnabled(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useMarketWatchEnabled() {
  const enterpriseId = useSelector((s) => s?.currentUser?.user?.enterpriseId || "");
  const isAdmin = useSelector((s) => !!s?.currentUser?.user?.isEnterpriseAdmin);
  const fresh = cache.loaded && cache.enterpriseId === enterpriseId;
  const [enabled, setEnabled] = useState(fresh ? cache.enabled : false);
  const [loading, setLoading] = useState(isAdmin && !fresh);

  useEffect(
    () =>
      subscribeMarketWatchEnabled((next) => {
        if (next.enterpriseId !== String(enterpriseId || "")) return;
        setEnabled(isAdmin && next.enabled === true);
        setLoading(!next.loaded);
      }),
    [enterpriseId, isAdmin]
  );

  useEffect(() => {
    if (!isAdmin || !enterpriseId) {
      setEnabled(false);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    if (!(cache.loaded && cache.enterpriseId === enterpriseId)) setLoading(true);
    loadMarketWatchEnabled(enterpriseId, isAdmin).then((on) => {
      if (cancelled) return;
      setEnabled(on);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [enterpriseId, isAdmin]);

  return { enabled, loading };
}
